import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  Sun,
  Wind,
} from 'lucide-react';

import type { WeatherKind } from '../wire';
import { useWorldModel } from '../world';
import styles from './clockhud.module.css';

/**
 * Clock HUD — bottom-right overlay showing "Day {day} -- {phase} -- {season}".
 * M2.1 deliverable #3. Reads `model.clock` via narrow zustand selectors.
 */
export function ClockHud(): JSX.Element {
  const day = useWorldModel((s) => s.model.clock.day);
  const phase = useWorldModel((s) => s.model.clock.phase);
  const season = useWorldModel((s) => s.model.clock.season);

  return (
    <div className={styles.clockHud} aria-label="World clock">
      <span className={styles.clockText}>
        Day {day + 1}
      </span>
      <span className={styles.dot} aria-hidden="true">
        &mdash;
      </span>
      <span className={styles.clockText}>{phase}</span>
      <span className={styles.dot} aria-hidden="true">
        &mdash;
      </span>
      <span className={styles.clockText}>{season}</span>
    </div>
  );
}

/**
 * Weather HUD — top-left indicator showing temperature + WeatherKind icon.
 * M2.1 deliverable #4. Reads `model.weather` via narrow zustand selectors.
 */
export function WeatherHud(): JSX.Element {
  const weatherKind = useWorldModel((s) => s.model.weather.kind);
  const temperature = useWorldModel((s) => s.model.weather.temperature);

  return (
    <div className={styles.weatherHud} aria-label="Weather conditions">
      <WeatherIcon kind={weatherKind} />
      <span className={styles.weatherTemp}>
        {Math.round(temperature)}&deg;C
      </span>
      <span className={styles.weatherLabel}>{formatWeatherKind(weatherKind)}</span>
    </div>
  );
}

function formatWeatherKind(kind: WeatherKind | 'Thunderstorm'): string {
  switch (kind) {
    case 'Clear':
      return 'Clear';
    case 'Rain':
      return 'Rain';
    case 'HeavyRain':
      return 'Heavy Rain';
    case 'Snow':
      return 'Snow';
    case 'Fog':
      return 'Fog';
    case 'Wind':
      return 'Windy';
    case 'Thunderstorm':
      return 'Thunderstorm';
    default:
      return 'Unknown';
  }
}

function WeatherIcon({ kind }: { kind: WeatherKind | 'Thunderstorm' }): JSX.Element {
  const common = { size: 18, strokeWidth: 2, 'aria-label': kind } as const;
  switch (kind) {
    case 'Clear':
      return <Sun {...common} color="#f4c23d" />;
    case 'Rain':
      return <CloudRain {...common} color="#88b5d6" />;
    case 'HeavyRain':
      return <CloudRain {...common} color="#5c8cb3" />;
    case 'Snow':
      return <CloudSnow {...common} color="#dbe7ef" />;
    case 'Fog':
      return <CloudFog {...common} color="#aeb5bd" />;
    case 'Wind':
      return <Wind {...common} color="#b8c3c9" />;
    case 'Thunderstorm':
      return <CloudLightning {...common} color="#9b7bd4" />;
    default:
      return <Cloud {...common} color="#9aa4b0" />;
  }
}
